import type { Map as MlMap, LngLatBoundsLike, PaddingOptions } from "maplibre-gl";
import { summaryState, onSummaryChange } from "./summary-store";
import { displayRiverName, riverDisplayLabel } from "./river-names";
import { downstreamIds, pathReachPoints } from "./sim-client";
import { setUserPinNear } from "./user-pin";

/**
 * v0.9.5c (owner's decision): on the map the 📍 and staffs outrank river names, so the name of
 * the user's river is ALSO given as a floating chip under the search box:
 *   "〰 <name by the card's rule> · ห่าง X กม."   — only when a main river is within 30 km
 * (the summary's "river" dimension; nothing published → no chip). Tap → fit the map to the pin +
 * that river. It sits under whatever is showing at the top (search box, the rain day-chips,
 * the storm pill, "จุดท่วมสูงสุด") — never on top of them. The same text feeds the 📍 label's
 * second line ("ใกล้<name> X กม.").
 */
const TOP_STACK = [".search-box", ".rf-chips", ".pr-days", ".storm-pill", ".top-fab"];
const FIT_REACHES = 40; // reaches of the same river shown when the chip is tapped

export function mountRiverChip(getMap: () => MlMap | null, pad: () => PaddingOptions): void {
  const chip = document.createElement("button");
  chip.type = "button";
  chip.id = "river-chip";
  chip.className = "river-chip";
  chip.hidden = true;
  document.body.append(chip);
  let reachId = 0, timer: number | null = null;

  const place = (): void => {
    if (chip.hidden) return;
    const box = document.querySelector(".search-box")?.getBoundingClientRect();
    if (!box) return;
    chip.style.left = `${box.left}px`;
    // the top-right group's column stays free (v0.9.9 A4)
    const col = document.querySelector(".tr-stack")?.getBoundingClientRect();
    const right = col && col.width > 0 && col.left < box.right ? col.left - 8 : box.right;
    chip.style.maxWidth = `${Math.max(120, right - box.left)}px`;
    const me = chip.getBoundingClientRect();
    let top = box.bottom;
    for (const q of TOP_STACK) {
      for (const el of document.querySelectorAll<HTMLElement>(q)) {
        const r = el.getBoundingClientRect(), cs = getComputedStyle(el);
        if (el.hidden || cs.display === "none" || cs.visibility === "hidden" || r.height === 0) continue;
        if (r.right <= box.left || r.left >= box.left + me.width) continue; // not above the chip's column
        top = Math.max(top, r.bottom);
      }
    }
    chip.style.top = `${Math.round(top + 6)}px`;
  };

  const render = (): void => {
    const s = summaryState();
    const riv = s.river.state === "ok" ? s.river.value : null;
    if (!s.from || !riv || !riv.name) {
      chip.hidden = true; setUserPinNear(null);
      if (timer !== null) { clearInterval(timer); timer = null; }
      return;
    }
    const name = riverDisplayLabel(riv.name), km = riv.distKm.toFixed(1);
    reachId = riv.reachId;
    chip.textContent = `〰 ${name} · ห่าง ${km} กม.`;
    chip.setAttribute("aria-label", `${name} ห่าง ${km} กิโลเมตร — แตะเพื่อดูแม่น้ำบนแผนที่`);
    setUserPinNear(`ใกล้${name} ${km} กม.${riv.nearest ? `\nลำน้ำใกล้สุด: ${riverDisplayLabel(riv.nearest.name)} ${riv.nearest.distKm.toFixed(1)} กม.` : ""}`);
    chip.hidden = false;
    place();
    // the top stack changes on its own (day-chips, storm pill) → keep the chip under it
    timer ??= window.setInterval(place, 600);
  };

  chip.addEventListener("click", () => {
    const m = getMap(), from = summaryState().from;
    if (!m || !from || !reachId) return;
    const name = displayRiverName(reachId);
    const ids: number[] = [];
    for (const id of downstreamIds(reachId, FIT_REACHES)) { if (ids.length && displayRiverName(id) !== name) break; ids.push(id); }
    const pts = pathReachPoints(ids).map(([, c]) => c);
    let w = from.lon, e = from.lon, s = from.lat, n = from.lat;
    for (const [x, y] of pts) { w = Math.min(w, x); e = Math.max(e, x); s = Math.min(s, y); n = Math.max(n, y); }
    const p = pad();
    // top: below the chip + the 📍 (36px) that stands above its point
    m.fitBounds([[w, s], [e, n]] as LngLatBoundsLike, { padding: { ...p, top: Math.max(p.top, chip.getBoundingClientRect().bottom + 56) }, maxZoom: 13, duration: 600 });
  });

  onSummaryChange(render);
  window.addEventListener("resize", place);
  window.addEventListener("fs-top-layout", place);
  render();
}
